import React, { useContext, useState } from 'react';

import AssetInventoryV2Context from '@totem/components/services/assetInventory/v2/AssetInventoryV2Context';
import {
  CHECK_GROUPS,
  CheckDefinition,
  CheckGroup,
  CHECKS,
  getCheckCount,
  isCheckApplicable,
  NO_ACTION_NEEDED,
  SEVERITIES,
  Severity,
  SEVERITY_DETAILS,
} from '@totem/components/services/assetInventory/v2/checks';
import { AssetInventorySummary } from '@totem/components/services/assetInventory/v2/types';

import './assetInventoryV2.css';

type Props = {
  // Printing shows every check expanded.
  expandAll?: boolean;
};

const managedPill = (isManaged: boolean) => (
  <span styleName={isManaged ? 'pill pill-green' : 'pill pill-grey'}>
    {isManaged ? 'Managed' : 'Not managed'}
  </span>
);

type SortState = { column: number; direction: 1 | -1 } | null;

const compareValues = (left: string | number, right: string | number) =>
  typeof left === 'number' && typeof right === 'number'
    ? left - right
    : String(left ?? '').localeCompare(String(right ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      });

// Sorts rows by one column, ties by the first column, so e.g. sorting by building keeps each building's assets in order.
const sortRows = <T,>(
  rows: T[],
  sort: SortState,
  valueOf: (row: T, column: number) => string | number,
) =>
  sort === null
    ? rows
    : rows
        .map((row, idx) => ({ row, idx }))
        .sort(
          (left, right) =>
            sort.direction *
              compareValues(
                valueOf(left.row, sort.column),
                valueOf(right.row, sort.column),
              ) ||
            compareValues(valueOf(left.row, 0), valueOf(right.row, 0)) ||
            left.idx - right.idx,
        )
        .map((item) => item.row);

const SortableHeading = ({
  title,
  column,
  sort,
  onSort,
}: {
  title: string;
  column: number;
  sort: SortState;
  onSort: (column: number) => void;
}) => {
  const isSorted = sort?.column === column;
  return (
    <th
      styleName="sortable"
      aria-sort={
        isSorted ? (sort.direction === 1 ? 'ascending' : 'descending') : 'none'
      }
    >
      <button
        type="button"
        styleName="sort-button"
        onClick={() => onSort(column)}
      >
        {title}
        {isSorted && (sort.direction === 1 ? ' ▲' : ' ▼')}
      </button>
    </th>
  );
};

const CheckTable = ({
  check,
  summary,
}: {
  check: CheckDefinition;
  summary: AssetInventorySummary;
}) => {
  const [sort, setSort] = useState<SortState>(null);
  const count = getCheckCount(check, summary);

  // A heading sorts ascending, then descending, then back to the check's own order.
  const onSort = (column: number) =>
    setSort((current) => {
      if (current?.column !== column) {
        return { column, direction: 1 };
      }
      return current.direction === 1 ? { column, direction: -1 } : null;
    });

  const hint = (
    <div styleName="sort-hint no-print">
      Click a column heading to sort, for example by building.
    </div>
  );

  if (check.table) {
    const { columns, rows } = check.table(summary);
    const sorted = sortRows(rows, sort, (row, column) => row[column]);
    return (
      <div styleName="table-wrap">
        {hint}
        <table styleName="mini-table">
          <thead>
            <tr>
              {columns.map((column, idx) => (
                <SortableHeading
                  key={column}
                  title={column}
                  column={idx}
                  sort={sort}
                  onSort={onSort}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr key={row.join('|')}>
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const rows = sortRows(
    summary.checks[check.key]?.rows ?? [],
    sort,
    (row, column) => check.columns[column].sortValue?.(row) ?? '',
  );

  return (
    <div styleName="table-wrap">
      {hint}
      <table styleName="mini-table">
        <thead>
          <tr>
            {check.columns.map((column, idx) => (
              <SortableHeading
                key={column.title}
                title={column.title}
                column={idx}
                sort={sort}
                onSort={onSort}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.deviceId}>
              {check.columns.map((column) => (
                <td key={column.title}>
                  {column.managed
                    ? managedPill(row.isManaged)
                    : column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {count > rows.length && (
        <div styleName="more-rows">
          + {count - rows.length} more · sorting covers the {rows.length} listed
        </div>
      )}
    </div>
  );
};

const FlaggedItemsTab = ({ expandAll = false }: Props) => {
  const { summary, openChecks, toggleCheck, showDetails } = useContext(
    AssetInventoryV2Context,
  );
  const [group, setGroup] = useState<CheckGroup | 'all'>('all');

  if (!summary) {
    return null;
  }

  const applicable = CHECKS.filter((check) =>
    isCheckApplicable(check, summary),
  );
  const open = applicable.filter((check) => getCheckCount(check, summary) > 0);
  const passed = applicable.filter(
    (check) => getCheckCount(check, summary) === 0,
  );
  const visible = open.filter(
    (check) => group === 'all' || check.group === group,
  );

  const severityTotals = (severity: Severity) => {
    const checks = open.filter((check) => check.severity === severity);
    return {
      checks: checks.length,
      count: checks.reduce(
        (total, check) => total + getCheckCount(check, summary),
        0,
      ),
    };
  };

  const passedLabels = [
    ...(summary.totals.noBuilding === 0
      ? ['Every asset is assigned to a building']
      : []),
    ...(summary.totals.computers > 0 && summary.totals.failedBackups === 0
      ? ['No failed computer backup jobs']
      : []),
    ...passed.map((check) => check.passed(summary)),
  ];

  return (
    <div styleName="pane">
      <div styleName="kpis">
        {SEVERITIES.map((severity) => {
          const { checks, count } = severityTotals(severity);
          return (
            <button
              key={severity}
              type="button"
              styleName="kpi"
              onClick={() =>
                document
                  .getElementById(`asset-inventory-severity-${severity}`)
                  ?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              <div styleName="kpi-label">{severity}</div>
              <div
                styleName="kpi-value"
                style={{ color: SEVERITY_DETAILS[severity].color }}
              >
                {count}
              </div>
              <div styleName="kpi-sub">
                {SEVERITY_DETAILS[severity].hint} · {checks} check
                {checks === 1 ? '' : 's'}
              </div>
            </button>
          );
        })}
      </div>

      <div styleName="chips no-print">
        {CHECK_GROUPS.map((option) => (
          <button
            key={option.key}
            type="button"
            styleName={group === option.key ? 'chip chip-on' : 'chip'}
            onClick={() => setGroup(option.key)}
          >
            {option.key === 'all' ? `All ${open.length} checks` : option.label}
          </button>
        ))}
        <span styleName="chips-note">
          Counted per check · one asset can appear in several
        </span>
      </div>

      {SEVERITIES.map((severity) => {
        const checks = visible.filter((check) => check.severity === severity);
        if (checks.length === 0) {
          return null;
        }
        const details = SEVERITY_DETAILS[severity];

        return (
          <div key={severity}>
            <div
              styleName="severity-head"
              id={`asset-inventory-severity-${severity}`}
            >
              <span styleName="dot" style={{ background: details.color }} />
              {severity}
              <span styleName="severity-hint">· {details.hint}</span>
            </div>
            {checks.map((check) => {
              const count = getCheckCount(check, summary);
              const isOpen = expandAll || openChecks.includes(check.key);

              return (
                <div
                  key={check.key}
                  id={`asset-inventory-check-${check.key}`}
                  styleName="finding"
                  style={{ borderLeftColor: details.color }}
                >
                  <button
                    type="button"
                    styleName="finding-top"
                    aria-expanded={isOpen}
                    onClick={() => toggleCheck(check.key)}
                  >
                    <div
                      styleName="finding-count"
                      style={{ color: details.color }}
                    >
                      {count}
                      <span styleName="finding-unit">{check.unit(count)}</span>
                    </div>
                    <div>
                      <div styleName="finding-title">{check.title}</div>
                      <div styleName="finding-why">
                        {check.why(summary, count)}
                      </div>
                    </div>
                    <span styleName="sub finding-top-wide">
                      {check.needFromYou === NO_ACTION_NEEDED
                        ? 'IB action'
                        : 'IB + you'}
                    </span>
                    <span styleName={`pill ${details.pill} finding-top-wide`}>
                      {severity}
                    </span>
                    <span
                      styleName={isOpen ? 'chevron chevron-open' : 'chevron'}
                    >
                      ›
                    </span>
                  </button>
                  {isOpen && (
                    <div styleName="finding-body">
                      <div styleName="actions">
                        <div>
                          <span styleName="actions-label">What IB will do</span>
                          {check.ibWillDo}
                        </div>
                        <div>
                          <span styleName="actions-label">
                            What we need from you
                          </span>
                          {check.needFromYou}
                        </div>
                      </div>
                      <CheckTable check={check} summary={summary} />
                      {check.detailsFilter !== false && (
                        <button
                          type="button"
                          styleName="link-button no-print"
                          onClick={() => showDetails(check.key)}
                        >
                          View in Details ›
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {passedLabels.length > 0 && (
        <>
          <div styleName="severity-head">
            <span styleName="dot" style={{ background: '#22c55e' }} />
            Checks passed
            <span styleName="severity-hint">
              · {passedLabels.length} check
              {passedLabels.length === 1 ? '' : 's'}
            </span>
          </div>
          <div styleName="passed">
            {passedLabels.map((label) => (
              <div key={label} styleName="pass">
                <span styleName="pass-check">✓</span>
                {label}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default FlaggedItemsTab;
